/* eslint-disable jsx-a11y/label-has-associated-control */
import {ICrpBpListItem} from 'api/business-partner';
import classNames from 'classnames';
import {CrpVariants} from 'constants/businessPartnerAttributes';
import {useAuth} from 'context/auth-context';

import FA from 'react-fontawesome';
import useModal from 'components/ui/ModalContainer/useModal';
import {useDeleteCrpBp, useGetConnectedCrps} from 'api/crp';
import OnboardingOverlay from 'components/ui/Overlays/OnboardingOverlay/OnboardingOverlay';
import {LegalType} from 'types/onboarding';
import {crpFormValidationSchema} from 'form/validation/businessPartner';
import useCrpTable from './hooks/useCrpTable';
import CrpForm from './CrpForm';
import AddNewCrpForm, {AddNewCrpBp} from './AddNewCrpForm';

import styles from './CrpTable.module.scss';
interface ICrpTableProps {
    crpList: ICrpBpListItem[];
    isCrpLoading: boolean;
    currentStep: number;
    legalType: LegalType | null;
    formOfControl?: number;
    inReview: boolean;
}
const CrpTable = ({
    crpList,
    isCrpLoading,
    currentStep,
    legalType,
    formOfControl,
    inReview,
}: ICrpTableProps) => {
    const {user} = useAuth();
    const {mutate: deleteCrp} = useDeleteCrpBp();
    const {handleCrpEdit, maxSignersReached} = useCrpTable({
        crpList,
        currentStep,
        isIndividual: legalType === LegalType.INDIVIDUAL,
    });
    const {openModalWithContent} = useModal();
    const {data: connectedCrps, isLoading: isConnectedCrpsLoading} =
        useGetConnectedCrps();
    const userCrp = crpList?.find(crpBp => crpBp.crp.user === user['@id']);

    const openEdit = (crp: ICrpBpListItem) => {
        openModalWithContent(
            'crpForm',
            <OnboardingOverlay fullHeight>
                <CrpForm
                    legalType={legalType}
                    crpBpId={crp?.['@id']}
                    handleSubmit={(values, setError, selectedRoles) => {
                        handleCrpEdit(values, selectedRoles, crp, setError);
                    }}
                    formOfControl={formOfControl?.toString()}
                    inReview={inReview}
                    crpList={crpList}
                />
            </OnboardingOverlay>,
        );
    };

    const handleAddNewCrp: AddNewCrpBp = crpBp => {
        if (!crpBp) {
            openModalWithContent(
                'crpForm',
                <OnboardingOverlay fullHeight>
                    <CrpForm
                        legalType={legalType}
                        handleSubmit={(val, setErr, selectedRoles) =>
                            handleCrpEdit(val, selectedRoles, undefined, setErr)
                        }
                        formOfControl={formOfControl?.toString()}
                        inReview={inReview}
                        crpList={crpList}
                    />
                </OnboardingOverlay>,
            );
        } else {
            openModalWithContent(
                'crpForm',
                <OnboardingOverlay fullHeight>
                    <CrpForm
                        legalType={legalType}
                        crpBpId={crpBp['@id']}
                        handleSubmit={(val, setErr, selectedRoles) =>
                            handleCrpEdit(val, selectedRoles, crpBp, setErr)
                        }
                        formOfControl={formOfControl?.toString()}
                        inReview={inReview}
                        crpList={crpList}
                    />
                </OnboardingOverlay>,
            );
        }
    };

    return (
        <div
            className={classNames(styles.crpTableWrapper, {
                [styles.loading]: isCrpLoading || isConnectedCrpsLoading,
            })}
        >
            {isCrpLoading ? (
                <div className={styles.loadingOverlay}>
                    <FA name="circle-o-notch" spin size="2x" />
                </div>
            ) : null}
            <div className={styles.crpTable}>
                <div className={styles.columns}>
                    <div className={styles.crpTableColumn}>
                        <div className={styles.crpTableHeader} />
                        {crpList?.map(crp => {
                            return (
                                <div
                                    key={`${crp['@id']}~nameCell`}
                                    className={classNames(
                                        styles.crpTableCell,
                                        styles.name,
                                    )}
                                    data-notranslate
                                >
                                    <div>
                                        {crp.crp.firstName} {crp.crp.lastName}{' '}
                                        <button
                                            type="button"
                                            className={styles.editCrp}
                                            onClick={() => openEdit(crp)}
                                        >
                                            <FA name="pencil" />
                                        </button>
                                    </div>
                                    {crp['@id'] === userCrp?.['@id'] ? null : (
                                        <button
                                            type="button"
                                            className={styles.deleteCrp}
                                            onClick={() =>
                                                deleteCrp(crp['@id'])
                                            }
                                        >
                                            <FA name="trash" />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.crpTableColumn}>
                        <div className={styles.crpTableHeader}>
                            Controlling person
                        </div>
                        {crpList?.map(crp => {
                            return (
                                <div
                                    key={`${crp['@id']}~ownerCell`}
                                    className={styles.crpTableCell}
                                >
                                    {crp.isOwner ? (
                                        <div className={styles.check} />
                                    ) : (
                                        ''
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.crpTableColumn}>
                        <div className={styles.crpTableHeader}>
                            Authorized person
                        </div>
                        {crpList?.map(crp => {
                            return (
                                <div
                                    key={`${crp['@id']}~signerCell`}
                                    className={styles.crpTableCell}
                                >
                                    {crp.hasSigningRights ? (
                                        <div className={styles.check} />
                                    ) : (
                                        ''
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.crpTableColumn}>
                        <div className={styles.crpTableHeader}>
                            Contract signer
                        </div>
                        {crpList?.map(crp => {
                            return (
                                <div
                                    key={`${crp['@id']}~contractSignerCell`}
                                    className={styles.crpTableCell}
                                >
                                    {crp.contractSigner ? (
                                        <div className={styles.check} />
                                    ) : (
                                        ''
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div className={styles.crpTableColumn}>
                        <div className={styles.crpTableHeader}>
                            Administrator
                        </div>
                        {crpList?.map(crp => {
                            return (
                                <div
                                    key={`${crp['@id']}~adminCell`}
                                    className={styles.crpTableCell}
                                >
                                    {crp.admin ? (
                                        <div className={styles.check} />
                                    ) : (
                                        ''
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
                <button
                    className={classNames(styles.addNewCrpButton, {
                        [styles.hidden]: legalType === LegalType.INDIVIDUAL,
                        // variant === CrpVariants.CONTRACT_SIGNER,
                    })}
                    type="button"
                    onClick={() => {
                        if (
                            connectedCrps?.length &&
                            legalType === LegalType.COMPANY
                        ) {
                            openModalWithContent(
                                'alert',
                                <OnboardingOverlay fullHeight>
                                    <AddNewCrpForm
                                        handleSubmit={handleAddNewCrp}
                                        connectedCrps={connectedCrps}
                                        crpBpList={crpList}
                                    />
                                </OnboardingOverlay>,
                            );
                        } else {
                            handleAddNewCrp();
                        }
                    }}
                >
                    <FA name="plus" /> Add person
                </button>
            </div>
        </div>
    );
};
export default CrpTable;
